"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { AlertSheet } from "./AlertSheet";
import type { IAlertContextValue, IShowAlertOptions } from "./types";

const AlertContext = createContext<IAlertContextValue | null>(null);

export function AlertProvider({ children }: { children: ReactNode }) {
  const [alertOptions, setAlertOptions] = useState<IShowAlertOptions | null>(null);
  const [open, setOpen] = useState(false);

  const dismissAlert = useCallback(() => {
    setOpen(false);
    setAlertOptions(null);
  }, []);

  const showAlert = useCallback((options: IShowAlertOptions) => {
    setAlertOptions(options);
    setOpen(true);
  }, []);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      alertOptions?.onClose?.();
      dismissAlert();
      return;
    }
    setOpen(nextOpen);
  };

  const wrappedActions = alertOptions?.actions?.map((action) => ({
    ...action,
    onClick: () => {
      action.onClick();
      dismissAlert();
    },
  }));

  const value = useMemo(
    () => ({ showAlert, dismissAlert }),
    [showAlert, dismissAlert]
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
      {alertOptions && (
        <AlertSheet
          {...alertOptions}
          actions={wrappedActions}
          open={open}
          onOpenChange={handleOpenChange}
        />
      )}
    </AlertContext.Provider>
  );
}

export function useAlert(): IAlertContextValue {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
}
